// @ts-nocheck

import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import {
  Bold,
  CheckSquare,
  Clock,
  FileText,
  Heading1,
  Heading2,
  Highlighter,
  Italic,
  Lightbulb,
  Link as LinkIcon,
  List,
  ListOrdered,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Quote,
  Redo,
  Save,
  StickyNote,
  Strikethrough,
  Trash2,
  Undo,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { GraphNoteOrFinding } from '@/services/api/graph'
import type { CaseNote } from '@/services/api/notes'
import { cn } from '@/utils/cn'

export interface CaseNotesListViewProps {
  notes: CaseNote[]
  graphItems: GraphNoteOrFinding[]
  loading: boolean
  currentUserName: string
  /** Persist a freshly composed note; resolves once written. */
  onAddNote: (content: string) => Promise<void>
  /** Persist an edit to an existing note. */
  onUpdateNote: (noteId: string, content: string) => Promise<void>
  /** Delete a note by id. */
  onDeleteNote: (noteId: string) => Promise<void>
}

// Color palette for authors. Decorative per-author avatar hues — there is no
// semantic design token for "distinct, arbitrary per-author colour", so these
// stay as raw palette utilities. frontend-rules-ok
const AUTHOR_COLORS = [
  'bg-cyan-600', // frontend-rules-ok
  'bg-purple-600', // frontend-rules-ok
  'bg-amber-600', // frontend-rules-ok
  'bg-emerald-600', // frontend-rules-ok
  'bg-rose-600', // frontend-rules-ok
  'bg-indigo-600', // frontend-rules-ok
  'bg-orange-600', // frontend-rules-ok
]

function getAuthorColor(authorName: string): string {
  let hash = 0
  for (let i = 0; i < authorName.length; i++) {
    hash = authorName.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AUTHOR_COLORS[Math.abs(hash) % AUTHOR_COLORS.length]
}

function getInitials(name: string): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Below md a long author name wraps beside the avatar (2rem = avatar + gap)
// instead of dropping to a line of its own.
const AUTHOR_NAME_CLASS =
  'text-sm font-medium text-foreground max-md:max-w-[calc(100%-2rem)] max-md:break-words'

function formatDate(dateString: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Component for displaying a graph note or finding
function GraphNoteCard({ item }: { item: GraphNoteOrFinding }) {
  const { t } = useTranslation()
  const isNote = item.type === 'note'
  const authorName = item.author_name || t('cases.notes.unknownUser')
  const authorColor = getAuthorColor(authorName)

  // Get display timestamp
  const timestamp = isNote
    ? item.created_at
    : (item as { finding_timestamp?: string }).finding_timestamp || item.created_at

  // Get content
  const content = isNote
    ? (item as { text: string }).text
    : (item as { description: string }).description

  // Get name if available
  const displayName = item.name

  // Check if edited
  const isEdited = item.updated_at && item.created_at !== item.updated_at

  return (
    <div className="border border-border rounded-lg bg-card/30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50 bg-card/50 max-md:gap-2 md:px-4">
        <div className="flex items-center gap-2 max-md:min-w-0 max-md:flex-wrap max-md:gap-y-0.5">
          <Avatar className="h-6 w-6">
            <AvatarFallback className={cn('text-[10px] text-foreground', authorColor)}>
              {getInitials(authorName)}
            </AvatarFallback>
          </Avatar>
          <span className={AUTHOR_NAME_CLASS}>{authorName}</span>
          <span className="text-muted-foreground max-md:hidden">•</span>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDate(timestamp)}
            {isEdited && <span className="text-muted-foreground">{t('cases.notes.edited')}</span>}
          </span>
        </div>

        <Badge
          variant="outline"
          className={cn(
            'text-[10px] h-5 max-md:shrink-0',
            isNote
              ? 'border-warning/50 text-warning bg-warning/10'
              : 'border-primary/50 text-primary bg-primary/10'
          )}
        >
          {isNote ? (
            <>
              <StickyNote className="h-3 w-3 mr-1" />
              {t('cases.notes.note')}
            </>
          ) : (
            <>
              <Lightbulb className="h-3 w-3 mr-1" />
              {t('cases.notes.finding')}
            </>
          )}
        </Badge>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Name/Title if available */}
        {displayName && <h4 className="font-medium text-foreground text-sm">{displayName}</h4>}

        {/* Main content */}
        <div className="prose prose-invert prose-sm max-w-none text-foreground max-md:break-words">
          {content}
        </div>

        {/* Attachments/Documentation */}
        {item.documentation && item.documentation.length > 0 && (
          <div className="pt-2 border-t border-border/50">
            <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              {t('cases.notes.attachments', { count: item.documentation.length })}
            </div>
            <div className="flex flex-wrap gap-2">
              {item.documentation.map((filePath, index) => {
                const fileName = filePath.split('/').pop() || filePath
                // Only absolute URLs (/api/v1/files/<s3_key> or http) are
                // servable; pre-v2 filesystem paths have no backing mount.
                const fileUrl =
                  filePath.startsWith('/api/') || filePath.startsWith('http') ? filePath : null
                if (!fileUrl) {
                  return (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-2 text-xs bg-muted rounded text-muted-foreground md:py-1"
                    >
                      <FileText className="h-3 w-3" />
                      {fileName}
                    </span>
                  )
                }
                return (
                  <a
                    key={index}
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-2 text-xs bg-muted hover:bg-accent rounded text-primary hover:text-primary/80 transition-colors md:py-1"
                  >
                    <FileText className="h-3 w-3" />
                    {fileName}
                  </a>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Individual note card component
function NoteCard({
  note,
  onEdit,
  onDelete,
  isEditing,
  onSaveEdit,
  onCancelEdit,
}: {
  note: CaseNote
  onEdit: () => void
  onDelete: () => void
  isEditing: boolean
  onSaveEdit: (content: string) => Promise<void>
  onCancelEdit: () => void
}) {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] } }),
      Placeholder.configure({ placeholder: t('cases.notes.editPlaceholder') }),
      Highlight,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: { class: 'text-primary underline hover:text-primary/80' },
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content: note.content,
    editable: isEditing,
    editorProps: {
      attributes: {
        class:
          'prose prose-invert prose-sm max-w-none focus:outline-none min-h-[80px] p-3 max-md:text-base',
      },
    },
  })

  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditing)
      if (isEditing) {
        editor.commands.setContent(note.content)
        setTimeout(() => editor.commands.focus('end'), 50)
      }
    }
  }, [isEditing, editor, note.content])

  const handleSave = async () => {
    if (!editor || isSaving) return
    setIsSaving(true)
    try {
      await onSaveEdit(editor.getHTML())
    } finally {
      setIsSaving(false)
    }
  }

  const authorColor = getAuthorColor(note.author_name)
  const isEdited = note.created_at !== note.updated_at

  return (
    <div className="group border border-border rounded-lg bg-card/30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50 bg-card/50 max-md:gap-2 md:px-4">
        <div className="flex items-center gap-2 max-md:min-w-0 max-md:flex-wrap max-md:gap-y-0.5">
          <Avatar className="h-6 w-6">
            <AvatarFallback className={cn('text-[10px] text-foreground', authorColor)}>
              {getInitials(note.author_name)}
            </AvatarFallback>
          </Avatar>
          <span className={AUTHOR_NAME_CLASS}>{note.author_name}</span>
          <span className="text-muted-foreground max-md:hidden">•</span>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDate(note.created_at)}
            {isEdited && <span className="text-muted-foreground">{t('cases.notes.edited')}</span>}
          </span>
        </div>

        {!isEditing && (
          <div className="flex items-center gap-1 transition-opacity max-md:shrink-0 md:opacity-0 md:group-hover:opacity-100 [@media(hover:none)]:opacity-100!">
            <Button
              variant="ghost"
              size="icon"
              onClick={onEdit}
              aria-label={t('common.edit')}
              className="size-9 text-muted-foreground hover:text-accent-foreground md:size-7"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              aria-label={t('common.delete')}
              className="size-9 text-muted-foreground hover:text-destructive md:size-7"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      {isEditing ? (
        <div>
          {/* Mini toolbar */}
          <div className="flex items-center gap-0.5 p-1.5 border-b border-border/50 bg-card/30 max-md:flex-wrap">
            <MiniToolbarButton
              onClick={() => editor?.chain().focus().toggleBold().run()}
              isActive={editor?.isActive('bold')}
              title={t('cases.notes.editor.bold')}
            >
              <Bold className="h-3.5 w-3.5" />
            </MiniToolbarButton>
            <MiniToolbarButton
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              isActive={editor?.isActive('italic')}
              title={t('cases.notes.editor.italic')}
            >
              <Italic className="h-3.5 w-3.5" />
            </MiniToolbarButton>
            <MiniToolbarButton
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              isActive={editor?.isActive('strike')}
              title={t('cases.notes.editor.strikethrough')}
            >
              <Strikethrough className="h-3.5 w-3.5" />
            </MiniToolbarButton>
            <MiniToolbarButton
              onClick={() => editor?.chain().focus().toggleHighlight().run()}
              isActive={editor?.isActive('highlight')}
              title={t('cases.notes.editor.highlight')}
            >
              <Highlighter className="h-3.5 w-3.5" />
            </MiniToolbarButton>
            <div className="w-px h-4 bg-border mx-0.5" />
            <MiniToolbarButton
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              isActive={editor?.isActive('bulletList')}
              title={t('cases.notes.editor.bulletList')}
            >
              <List className="h-3.5 w-3.5" />
            </MiniToolbarButton>
            <MiniToolbarButton
              onClick={() => editor?.chain().focus().toggleTaskList().run()}
              isActive={editor?.isActive('taskList')}
              title={t('cases.notes.editor.taskList')}
            >
              <CheckSquare className="h-3.5 w-3.5" />
            </MiniToolbarButton>

            {/* Below md the spacer takes a whole line, so Cancel + Save wrap together under the formatting buttons. */}
            <div className="flex-1 max-md:basis-full" />

            <Button
              variant="ghost"
              size="sm"
              onClick={onCancelEdit}
              className="h-9 px-2 text-xs text-muted-foreground hover:text-accent-foreground max-md:ml-auto md:h-6"
            >
              <X className="h-3 w-3 mr-1" />
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              variant="solid"
              className="h-9 px-2 text-xs md:h-6"
            >
              {isSaving ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Save className="h-3 w-3 mr-1" />
              )}
              {t('common.save')}
            </Button>
          </div>
          <EditorContent editor={editor} />
        </div>
      ) : (
        <div
          className="prose prose-invert prose-sm max-w-none p-3 cursor-pointer hover:bg-muted/20 transition-colors max-md:break-words md:p-4"
          onClick={onEdit}
          dangerouslySetInnerHTML={{ __html: note.content }}
        />
      )}
    </div>
  )
}

function MiniToolbarButton({
  onClick,
  isActive,
  children,
  title,
}: {
  onClick: () => void
  isActive?: boolean
  children: React.ReactNode
  title: string
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={cn(
        'p-2.5 rounded transition-colors md:p-1',
        isActive
          ? 'bg-primary/20 text-primary'
          : 'text-muted-foreground hover:text-accent-foreground hover:bg-accent'
      )}
    >
      {children}
    </button>
  )
}

// New note composer
function NewNoteComposer({
  onSubmit,
  onCancel,
  authorName,
}: {
  onSubmit: (content: string) => Promise<void>
  onCancel: () => void
  authorName: string
}) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] } }),
      Placeholder.configure({ placeholder: t('cases.notes.writePlaceholder') }),
      Highlight,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: { class: 'text-primary underline hover:text-primary/80' },
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class:
          'prose prose-invert prose-sm max-w-none focus:outline-none min-h-[120px] p-4 max-md:text-base',
      },
    },
  })

  useEffect(() => {
    setTimeout(() => editor?.commands.focus(), 100)
  }, [editor])

  const handleSubmit = async () => {
    if (!editor || isSubmitting) return
    const content = editor.getHTML()
    if (!content || content === '<p></p>') return

    setIsSubmitting(true)
    try {
      await onSubmit(content)
    } finally {
      setIsSubmitting(false)
    }
  }

  const ToolbarButton = ({
    onClick,
    isActive,
    disabled,
    children,
    title,
  }: {
    onClick: () => void
    isActive?: boolean
    disabled?: boolean
    children: React.ReactNode
    title?: string
  }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'p-2.5 rounded transition-colors md:p-1.5',
        isActive
          ? 'bg-primary/20 text-primary'
          : 'text-muted-foreground hover:text-accent-foreground hover:bg-accent',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {children}
    </button>
  )

  return (
    <div className="border border-primary/40 rounded-lg bg-card/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border/50 bg-card/70">
        <Avatar className="h-6 w-6">
          <AvatarFallback className={cn('text-[10px] text-foreground', getAuthorColor(authorName))}>
            {getInitials(authorName)}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium text-foreground">{authorName}</span>
        <span className="text-xs text-muted-foreground">• {t('cases.notes.newNote')}</span>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-border/50 bg-card/50">
        <ToolbarButton
          onClick={() => editor?.chain().focus().undo().run()}
          disabled={!editor?.can().undo()}
          title={t('cases.notes.editor.undo')}
        >
          <Undo className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().redo().run()}
          disabled={!editor?.can().redo()}
          title={t('cases.notes.editor.redo')}
        >
          <Redo className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1 max-md:hidden" />

        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBold().run()}
          isActive={editor?.isActive('bold')}
          title={t('cases.notes.editor.bold')}
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          isActive={editor?.isActive('italic')}
          title={t('cases.notes.editor.italic')}
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          isActive={editor?.isActive('strike')}
          title={t('cases.notes.editor.strikethrough')}
        >
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleHighlight().run()}
          isActive={editor?.isActive('highlight')}
          title={t('cases.notes.editor.highlight')}
        >
          <Highlighter className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1 max-md:hidden" />

        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor?.isActive('heading', { level: 1 })}
          title={t('cases.notes.editor.heading1')}
        >
          <Heading1 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor?.isActive('heading', { level: 2 })}
          title={t('cases.notes.editor.heading2')}
        >
          <Heading2 className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1 max-md:hidden" />

        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          isActive={editor?.isActive('bulletList')}
          title={t('cases.notes.editor.bulletList')}
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          isActive={editor?.isActive('orderedList')}
          title={t('cases.notes.editor.numberedList')}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleTaskList().run()}
          isActive={editor?.isActive('taskList')}
          title={t('cases.notes.editor.taskList')}
        >
          <CheckSquare className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          isActive={editor?.isActive('blockquote')}
          title={t('cases.notes.editor.quote')}
        >
          <Quote className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            const url = window.prompt(t('cases.notes.editor.enterUrl'))
            if (url) editor?.chain().focus().setLink({ href: url }).run()
          }}
          isActive={editor?.isActive('link')}
          title={t('cases.notes.editor.addLink')}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-border/50 bg-card/70">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="text-muted-foreground hover:text-accent-foreground max-md:h-9"
        >
          <X className="h-4 w-4 mr-1" />
          {t('common.cancel')}
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting}
          variant="solid"
          className="max-md:h-9"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          {t('cases.notes.addNote')}
        </Button>
      </div>
    </div>
  )
}

/**
 * Pure presentation for the case notes panel: renders graph notes/findings
 * (read-only) and editable PostgreSQL notes, plus the new-note composer and
 * delete-confirmation dialog. All persistence is delegated to the
 * `onAddNote` / `onUpdateNote` / `onDeleteNote` callbacks supplied by the
 * connected `CaseNotesList` in `features/case-notes`.
 */
export function CaseNotesListView({
  notes,
  graphItems,
  loading,
  currentUserName,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
}: CaseNotesListViewProps) {
  const { t } = useTranslation()
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null)

  const handleAddNote = async (content: string) => {
    await onAddNote(content)
    setIsAddingNote(false)
  }

  const handleUpdateNote = async (noteId: string, content: string) => {
    await onUpdateNote(noteId, content)
    setEditingNoteId(null)
  }

  const handleDeleteNote = async (noteId: string) => {
    await onDeleteNote(noteId)
    setDeleteNoteId(null)
  }

  return (
    // Below md the panel's own frame and padding are dropped: it sits inside the
    // report page's card, and the notes need the full width.
    <Card className="bg-card/50 border-border max-md:border-0 max-md:bg-transparent max-md:py-0 max-md:gap-4!">
      <CardHeader className="max-md:px-0 md:pb-3">
        <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-3">
          <div className="flex items-center gap-3 max-md:min-w-0">
            <div className="p-2 rounded-lg bg-muted max-md:shrink-0">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="max-md:min-w-0">
              <CardTitle className="text-lg text-foreground">{t('cases.notes.title')}</CardTitle>
              <CardDescription className="text-muted-foreground">
                {t('cases.notes.description')}
              </CardDescription>
            </div>
          </div>

          {!isAddingNote && (
            <Button
              onClick={() => setIsAddingNote(true)}
              size="sm"
              variant="solid"
              className="gap-2 max-md:h-9"
            >
              <Plus className="h-4 w-4" />
              {t('cases.notes.addNote')}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4 max-md:px-0">
        {/* New note composer */}
        {isAddingNote && (
          <NewNoteComposer
            onSubmit={handleAddNote}
            onCancel={() => setIsAddingNote(false)}
            authorName={currentUserName}
          />
        )}

        {/* Notes and Findings list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : graphItems.length === 0 && notes.length === 0 && !isAddingNote ? (
          <div
            className="py-12 flex flex-col items-center justify-center text-center border border-dashed border-border rounded-lg hover:border-accent transition-colors cursor-pointer"
            onClick={() => setIsAddingNote(true)}
          >
            <FileText className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground mb-2">{t('cases.notes.noNotes')}</p>
            <p className="text-sm text-muted-foreground">{t('cases.notes.clickToAdd')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Graph Notes and Findings (from Neo4j) */}
            {graphItems.map(item => (
              <GraphNoteCard key={item.id} item={item} />
            ))}

            {/* Database Notes (from PostgreSQL) - shown below graph items */}
            {notes.map(note => (
              <NoteCard
                key={note.id}
                note={note}
                isEditing={editingNoteId === note.id}
                onEdit={() => setEditingNoteId(note.id)}
                onDelete={() => setDeleteNoteId(note.id)}
                onSaveEdit={content => handleUpdateNote(note.id, content)}
                onCancelEdit={() => setEditingNoteId(null)}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteNoteId} onOpenChange={() => setDeleteNoteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {t('cases.notes.deleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {t('cases.notes.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-muted border-border text-foreground hover:bg-accent">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteNoteId && handleDeleteNote(deleteNoteId)}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
